import React, { useEffect, useState } from "react";
import { fetchEvents, createEvent } from "../../../services/aro/eventService";
import { useAuth } from "../../../context/useAuth";
import { AROEvent } from "../../../types/aro";
import AROBadge       from "../shared/AROBadge/AROBadge";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState     from "../shared/EmptyState/EmptyState";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import { formatDateLong } from "../../../utils/formatters";
import "./EventsPage.css";

interface Props {
  onViewCertificates: (eventId: number) => void;
}

interface CreateForm {
  event_name: string;
  start_date: string;
  end_date:   string;
}

function formatEventDateRange(ev: AROEvent): string {
  if (ev.end_date) return `${formatDateLong(ev.start_date)} – ${formatDateLong(ev.end_date)}`;
  return formatDateLong(ev.start_date);
}

export default function EventsPage({ onViewCertificates }: Props) {
  const { email } = useAuth();
  const [events,     setEvents]     = useState<AROEvent[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [showModal,  setShowModal]  = useState(false);
  const [form,       setForm]       = useState<CreateForm>({ event_name: "", start_date: "", end_date: "" });
  const [formErrors, setFormErrors] = useState<Partial<CreateForm>>({});
  const [creating,   setCreating]   = useState(false);
  const [createErr,  setCreateErr]  = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    fetchEvents()
      .then((r) => setEvents(r.events))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openModal = () => {
    setForm({ event_name: "", start_date: "", end_date: "" });
    setFormErrors({});
    setCreateErr(null);
    setShowModal(true);
  };

  const validate = (): boolean => {
    const errs: Partial<CreateForm> = {};
    if (!form.event_name.trim()) errs.event_name = "Event name is required.";
    if (!form.start_date)        errs.start_date = "Start date is required.";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) return;
    setCreating(true);
    setCreateErr(null);
    try {
      await createEvent(form.event_name.trim(), form.start_date, email, form.end_date || undefined);
      setShowModal(false);
      load();
    } catch (e: any) {
      setCreateErr(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="aro-page">
      <div className="aro-page-header-row">
        <PageHeader title="Events" subtitle={`${events.length} AI Week events`} />
        <button className="aro-btn aro-btn--primary" onClick={openModal}>+ New Event</button>
      </div>

      {error && <div className="aro-error-banner">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : events.length === 0 ? (
        <EmptyState icon="📅" message="No events yet. Create your first AI Week event." />
      ) : (
        <div className="aro-events-grid">
          {events.map((ev) => (
            <div key={ev.id} className="aro-event-card">
              <h3 className="aro-event-card__name">{ev.event_name}</h3>
              <div className="aro-event-card__meta">
                <span>📅 {formatEventDateRange(ev)}</span>
                <AROBadge label={`${ev.cert_count} certificates`} variant="blue" />
              </div>
              <div className="aro-event-card__footer">
                <button
                  className="aro-link-btn"
                  onClick={() => onViewCertificates(ev.id)}
                >
                  View Certificates →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Event Modal */}
      {showModal && (
        <div className="aro-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="aro-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="aro-modal__title">New Event</h2>

            <div className="aro-modal__field">
              <label className="aro-modal__label aro-modal__label--required">Event Name</label>
              <input
                className="aro-input"
                value={form.event_name}
                onChange={(e) => setForm((f) => ({ ...f, event_name: e.target.value }))}
                placeholder="Ohio AI Week Spring 2026"
              />
              {formErrors.event_name && <span className="aro-modal__field-err">{formErrors.event_name}</span>}
            </div>

            <div className="aro-modal__field">
              <label className="aro-modal__label aro-modal__label--required">Start Date</label>
              <input
                className="aro-input"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              />
              {formErrors.start_date && <span className="aro-modal__field-err">{formErrors.start_date}</span>}
            </div>

            <div className="aro-modal__field">
              <label className="aro-modal__label">End Date <span className="aro-muted">(optional — leave blank for single-day)</span></label>
              <input
                className="aro-input"
                type="date"
                value={form.end_date}
                min={form.start_date || undefined}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              />
            </div>

            {createErr && <div className="aro-error-banner">{createErr}</div>}

            <div className="aro-modal__actions">
              <button className="aro-btn aro-btn--ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="aro-btn aro-btn--primary" onClick={handleCreate} disabled={creating}>
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
